/**
 * Renders a `Patch` into samples with plain arithmetic - no WebAudio - so every sound in the
 * game can be rendered, measured and unit-tested in node. The browser copies the result into
 * an `AudioBuffer` once and plays it as often as it likes.
 */

import { Svf } from './filter';
import { DEFAULT_PEAK, EDGE_IN_SECONDS, EDGE_OUT_SECONDS, type Layer, type Patch, type Wave } from './patch';
import { mulberry32, type Rand } from './rng';

const TAU = Math.PI * 2;
/** Pitch slides and filter sweeps are recomputed this often (samples): control rate. */
const CONTROL = 16;
const MIN_ATTACK = 0.0008;
const MIN_DECAY = 0.002;

function noiseSource(wave: Wave, rand: Rand): () => number {
  if (wave === 'pink') {
    // Paul Kellet's economy pink filter: three leaky integrators.
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    return () => {
      const w = rand() * 2 - 1;
      b0 = 0.99765 * b0 + w * 0.099046;
      b1 = 0.963 * b1 + w * 0.2965164;
      b2 = 0.57 * b2 + w * 1.0526913;
      return (b0 + b1 + b2 + w * 0.1848) * 0.25;
    };
  }
  if (wave === 'brown') {
    let last = 0;
    return () => {
      last = (last + 0.02 * (rand() * 2 - 1)) / 1.02;
      return last * 3.5;
    };
  }
  return () => rand() * 2 - 1;
}

function envelopeAt(t: number, attack: number, hold: number, decay: number, curve: number): number {
  if (t < attack) return t / attack;
  if (t < attack + hold) return 1;
  const x = 1 - (t - attack - hold) / decay;
  if (x <= 0) return 0;
  return curve === 1 ? x : curve === 2 ? x * x : curve === 3 ? x * x * x : Math.pow(x, curve);
}

interface Variation {
  pitch: number;
  time: number;
  gain: number;
}

/** Seconds a layer lasts, from its own start. */
function layerSeconds(layer: Layer, time: number): number {
  const attack = Math.max(MIN_ATTACK, (layer.attack ?? 0) * time);
  return attack + (layer.hold ?? 0) * time + Math.max(MIN_DECAY, layer.decay * time);
}

/** Length of the rendered sound in seconds, before any per-variant time jitter. */
export function patchSeconds(patch: Patch, time = 1): number {
  let end = 0;
  for (const layer of patch.layers) end = Math.max(end, (layer.at ?? 0) * time + layerSeconds(layer, time));
  const echo = patch.echo ? patch.echo.time * (patch.echo.count ?? 1) : 0;
  return end + echo + EDGE_OUT_SECONDS;
}

/** `vary`: this variant's pitch, time and gain multipliers. */
function renderLayer(out: Float32Array, layer: Layer, sampleRate: number, rand: Rand, vary: Variation): void {
  const { pitch, time } = vary;
  const gain = layer.gain * vary.gain;
  const attack = Math.max(MIN_ATTACK, (layer.attack ?? 0) * time);
  const hold = (layer.hold ?? 0) * time;
  const decay = Math.max(MIN_DECAY, layer.decay * time);
  const total = attack + hold + decay;
  const curve = layer.curve ?? 2;
  const start = Math.round((layer.at ?? 0) * time * sampleRate);
  const count = Math.min(out.length - start, Math.floor(total * sampleRate));

  const isNoise = layer.wave === 'noise' || layer.wave === 'pink' || layer.wave === 'brown';
  const noise = isNoise ? noiseSource(layer.wave, rand) : null;
  const f0 = (layer.freq ?? 440) * pitch;
  const lnSlide = layer.freqEnd !== undefined ? Math.log((layer.freqEnd * pitch) / f0) : 0;
  const sweep = layer.sweep ?? 1;
  const duty = layer.duty ?? 0.5;
  const vibDepth = layer.vibDepth ?? 0;
  const vibHz = layer.vibHz ?? 5;
  const fmIndex = layer.fmIndex ?? 0;
  const fmRatio = layer.fmRatio ?? 2;
  const fmDecay = layer.fmDecay ?? total;
  const tremDepth = layer.tremDepth ?? 0;
  const tremHz = layer.tremHz ?? 0;
  const drive = layer.drive ?? 0;
  const driveNorm = drive > 0 ? 1 / Math.tanh(drive) : 1;
  const crushStep = layer.crush !== undefined ? layer.crush / sampleRate : 0;

  const spec = layer.filter;
  const svf = spec ? new Svf() : null;
  const q = spec?.q ?? 0.707;
  const lnCut = spec?.cutoffEnd !== undefined ? Math.log(spec.cutoffEnd / spec.cutoff) : 0;
  if (svf && spec) svf.set(spec.cutoff * (isNoise ? 1 : pitch), q, sampleRate);

  // Every tone starts at zero phase: thumps punch the same way each time, and layers that share
  // a pitch (a bark's two formants) stay coherent. Variation comes from `jitter`, not from phase.
  let phase = 0;
  let modPhase = 0;
  let freq = f0;
  let crushAcc = 1;
  let held = 0;
  for (let i = 0; i < count; i++) {
    const t = i / sampleRate;
    if (i % CONTROL === 0) {
      const u = t / total;
      freq = lnSlide !== 0 ? f0 * Math.exp(lnSlide * (sweep === 1 ? u : Math.pow(u, sweep))) : f0;
      if (vibDepth !== 0) freq *= Math.pow(2, (vibDepth * Math.sin(TAU * vibHz * t)) / 12);
      if (svf && spec && lnCut !== 0) svf.set(spec.cutoff * Math.exp(lnCut * u), q, sampleRate);
    }
    let s: number;
    if (noise) s = noise();
    else {
      let inc = freq / sampleRate;
      if (fmIndex !== 0) {
        inc *= 1 + fmIndex * Math.exp(-t / fmDecay) * Math.sin(TAU * modPhase);
        modPhase += (freq * fmRatio) / sampleRate;
        modPhase -= Math.floor(modPhase);
      }
      phase += inc;
      phase -= Math.floor(phase);
      s = layer.wave === 'sine' ? Math.sin(TAU * phase)
        : layer.wave === 'triangle' ? 4 * Math.abs(phase - 0.5) - 1
        : layer.wave === 'saw' ? 2 * phase - 1
        : phase < duty ? 1 : -1;
    }
    if (crushStep > 0) {
      crushAcc += crushStep;
      if (crushAcc >= 1) {
        crushAcc -= 1;
        held = s;
      }
      s = held;
    }
    if (svf && spec) s = svf.process(s, spec.type);
    if (drive > 0) s = Math.tanh(s * drive) * driveNorm;
    let amp = envelopeAt(t, attack, hold, decay, curve) * gain;
    if (tremDepth !== 0) {
      const lfo = Math.sin(TAU * tremHz * t);
      amp *= 1 - tremDepth * (layer.tremSquare === true ? (lfo >= 0 ? 0 : 1) : 0.5 - 0.5 * lfo);
    }
    out[start + i] = (out[start + i] ?? 0) + s * amp;
  }
}

/** Mix-level treatment: slap-back taps, drive, DC removal, click-free edges, peak normalisation. */
function finish(out: Float32Array, patch: Patch, sampleRate: number): void {
  const n = out.length;
  if (patch.echo) {
    const dry = out.slice();
    const taps = patch.echo.count ?? 1;
    for (let k = 1; k <= taps; k++) {
      const offset = Math.round(patch.echo.time * k * sampleRate);
      const g = Math.pow(patch.echo.gain, k);
      for (let i = 0; i + offset < n; i++) out[i + offset] = (out[i + offset] ?? 0) + (dry[i] ?? 0) * g;
    }
  }
  const drive = patch.drive ?? 0;
  // A pulse wave or a driven thump carries DC, which would click at the edges: block it at 20 Hz.
  const r = 1 - (TAU * 20) / sampleRate;
  let prevIn = 0;
  let prevOut = 0;
  let peak = 0;
  const edgeIn = Math.max(1, Math.round(EDGE_IN_SECONDS * sampleRate));
  const edgeOut = Math.max(1, Math.round(EDGE_OUT_SECONDS * sampleRate));
  for (let i = 0; i < n; i++) {
    let s = out[i] ?? 0;
    if (drive > 0) s = Math.tanh(s * drive);
    const y = s - prevIn + r * prevOut;
    prevIn = s;
    prevOut = y;
    let edge = 1;
    if (i < edgeIn) edge = i / edgeIn;
    if (i >= n - edgeOut) edge = Math.min(edge, (n - 1 - i) / edgeOut);
    const v = y * edge;
    out[i] = v;
    const a = Math.abs(v);
    if (a > peak) peak = a;
  }
  if (peak < 1e-9) return;
  const scale = (patch.peak ?? DEFAULT_PEAK) / peak;
  for (let i = 0; i < n; i++) out[i] = (out[i] ?? 0) * scale;
}

/**
 * Render one variant of a patch. The same `(patch, sampleRate, seed)` always gives the same
 * samples; different seeds differ in their noise and, by the patch's `jitter`, in pitch,
 * length and layer balance - which is what stops a burst sounding like a machine.
 */
export function renderPatch(patch: Patch, sampleRate: number, seed: number): Float32Array<ArrayBuffer> {
  const rand = mulberry32(seed);
  const spread = (amount: number | undefined): number => 1 + (rand() * 2 - 1) * (amount ?? 0);
  const pitch = spread(patch.jitter?.pitch);
  const time = spread(patch.jitter?.time);
  const out = new Float32Array(Math.ceil(patchSeconds(patch, time) * sampleRate));
  for (const layer of patch.layers) {
    renderLayer(out, layer, sampleRate, rand, { pitch, time, gain: spread(patch.jitter?.gain) });
  }
  finish(out, patch, sampleRate);
  return out;
}
